import { DrObject, DrObjectType, SupportedRegExType } from '../../../models/dr-object';
import { Component, ViewChild, OnInit, ViewEncapsulation } from '@angular/core';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { isString, isFunction, isUndefined, isNullOrUndefined } from '../../../models/utils';
import { CommonService } from '../../../services/common/common.service';
import { RestService } from '../../../services/rest/rest.service';
import { Endpoint, MessageType, UpdateStatusType, HelpIndex, ReviewSystem } from '../../../models/enums';
import { NameDescriptionComponent } from '../../name-description/name-description.component';
import { Project, ProjectViewModel } from '../../../models/project';
import { Corpus, CorpusViewModel } from '../../../models/corpus';
import { Template, TemplateViewModel } from '../../../models/template';
import { AliasList, AliasListViewModel } from '../../../models/alias-lists';
import { CustomFieldViewModel, CustomField, CustomFieldProfile } from '../../../models/custom-fields';
import { DomainList, DomainListViewModel } from '../../../models/domain-lists';
import { LoadFileProjectTemplate, LoadFileProjectTemplateViewModel } from '../../../models/load-file-project-template';
import { Custodian, CustodianViewModel } from '../../../models/custodian';
import { Profile, ProfileViewModel } from '../../../models/profile';
import { SavedSearch, SavedSearchViewModel } from '../../../models/saved-searches';
import { ExportMetadataProfile, ExportMetadataProfileViewModel } from '../../../models/export-metadata-profile';
import { Workflow, WorkflowViewModel } from '../../../models/workflow';
import { Role, RoleViewModel, } from '../../../models/role';
import { ExportStream, ExportStreamViewModel } from '../../../models/export-stream';
import * as _ from 'lodash';
import { MediaID, MediaIDViewModel } from '../../../models/mediaid';
import { Batch, BatchViewModel } from '../../../models/batch';
import { Service, ServiceViewModel } from '../../../models/service';
import { DataArea, DataAreaViewModel } from '../../../models/data-area';
import { Organization, OrganizationViewModel } from '../../../models/organization';
import { SelectReviewProjectsModalComponent } from '../select-review-projects-modal/select-review-projects-modal.component';


class CreateEditEntityDataType extends DrObject {

    entity: any;

    isEdit: boolean;
    validateName = true;

    request: any;
    validateNameRequest: {
        endpoint: Endpoint,
        request: (name: string) => void
    };
    endpoint: Endpoint;

    title: string;
    isBusyMessage: string;
    helpIndex?: HelpIndex;
    updateStatusType?: UpdateStatusType;
    autoSelectEntity?: boolean;
    responseObjectName?: string;
    responseObjectHandleName = 'handle';
    originalPriority: number;
    originalSelectedReviewProjects: any[];
    originalFileMD5: string;
    originalReviewSystem: string;

    constructor(value?: Partial<CreateEditEntityDataType>) {
        super(value.objectType, value.regex);
        if (value) {
            Object.assign(this, value);

            this.entity = value.entity;
            const name = this.getBusyMessageByObjectType(value.objectType);
            let title = name.replace(/([a-z](?=[A-Z]))/g, '$1 ').replace(/\b\w/g, l => l.toUpperCase());
            title = title.charAt(0) + title.slice(1).toLowerCase();

            this.isEdit = false;
            if ((this.entity && this.entity.handle) || (this.title === 'Edit Template')) {
                if (this.entity.handle !== 'autoSelect' || (this.objectType === DrObjectType.LOAD_FILE_IMPORT && this.entity.id !== 'autoSelect')) {
                    this.isEdit = true;
                    if (this.isEdit) {
                        if (!this.title) {
                            this.title = `Edit ${title}`;
                        }
                        if (!this.isBusyMessage) {
                            this.isBusyMessage = `Updating ${title} ...`;
                        }
                    }
                } else {
                    this.setBusyMessage(title);
                }
            } else {
                if (!this.title) {
                    this.title = this.objectType !== DrObjectType.TEMPLATE ? `Create ${title}` : `New ${title}`;
                }
                this.setBusyMessage(title);
            }
        }
    }

    getBusyMessageByObjectType(type: DrObjectType): string {
        switch (type) {
            case DrObjectType.WORK_FLOWS: return 'Workflow';
            case DrObjectType.SAVED_SEARCH: return 'Saved Search';
            case DrObjectType.LOAD_FILE_IMPORT: return 'Load File Settings';
            case DrObjectType.ALIAS_LIST: return 'Alias List';
            case DrObjectType.EXPORT_FIELDS: return 'Export Fields';
            case DrObjectType.CUSTOM_FIELD: return 'Custom Field';
            case DrObjectType.DOMAIN_LIST: return 'Domain List';
        }
        return type;
    }

    setBusyMessage(title: string): void {
        if (!this.isBusyMessage) {
            title = title === 'Profile' ? 'Synthetic Document' : title;
            this.isBusyMessage = `Creating ${title} ...`;
        }
    }
}

@Component({
    standalone: false,
    selector: 'res-create-edit-entity',
    templateUrl: './create-edit-entity-modal.component.html',
    styleUrls: ['./create-edit-entity-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class CreateEditEntityModalComponent extends BaseModalComponent implements OnInit {
    @ViewChild(NameDescriptionComponent, { static: true }) nameDescriptionComponent: NameDescriptionComponent;
    isBusy = false;
    isBusyMessage: string;
    entityData: CreateEditEntityDataType;
    modalClass = 'create-edit-entity-modal';
    modalAutomationId = 'create-edit-entity';
    popoverStickyClass: string;
    isExpressSystem = false;
    isExpressWithReviewProject = false;
    reviewSystem: string;
    reviewSystemEnum = ReviewSystem;
    reefReviewEnabled = false;
    standardProjectType = true;
    availableReviewProjects = [];
    selectedReviewProjects = [];
    logoModel = { encodedData: null, fileName: '', fileMD5: '' };
    userValid = true;
    reefReviewDisplayName = ReviewSystem.REEF_REVIEW.split(/(?=[A-Z])/).join(" ");
    modalOpened;

    constructor(
        private modalService: BsModalService,
        public editEntityModalRef: BsModalRef,
        private restService: RestService,
        private commonService: CommonService) {
        super(modalService, editEntityModalRef);
    }

    ngOnInit(): void {
        this.modalOpened = true;
        this.loadEntityData();
    }

    loadEntityData(): void {
        if (this.modalData) {
            if (this.modalData.data instanceof Project) {
                const project = _.cloneDeep( this.modalData.data) as Project;
                this.isExpressSystem = this.modalData.isExpressSystem;
                this.reefReviewEnabled = this.commonService.reefReviewEnabled;
                this.modalClass += ' project-modal';
                if (project.reviewProjects?.length > 0) {
                    this.selectedReviewProjects = project.reviewProjects;
                    if (project.selfServiceProject) {
                        this.isExpressWithReviewProject = true;
                    }
                }
                
                this.reviewSystem = project.reviewSystem ? project.reviewSystem : ReviewSystem.NONE;
                this.logoModel.fileMD5 = project.attributes.find(o => o.name === 'PROJECT_LOGO_FILE_MD5')?.value || '';
                this.logoModel.fileName = project.attributes.find(o => o.name === 'PROJECT_LOGO_FILE_NAME')?.value || '';
                const projectViewModel = new ProjectViewModel(project);
                if (this.reviewSystem === ReviewSystem.REEF_REVIEW) {
                    this.onChange(this.reviewSystem);
                }
                this.entityData = new CreateEditEntityDataType({
                    entity: project,
                    objectType: projectViewModel.objectType,
                    regex: projectViewModel.regex,
                    endpoint: Endpoint.projectManager_updateProject,
                    originalSelectedReviewProjects: _.clone(this.selectedReviewProjects),
                    originalReviewSystem: this.reviewSystem,
                    originalFileMD5: this.logoModel.fileMD5,
                    title: isString(project.name) ? 'Edit Project' : 'New Project',
                    request: () => {
                        let retVal = {
                            name: this.nameDescriptionComponent.name,
                            projectHandle: project.handle,
                            description: this.nameDescriptionComponent.description,
                            organizationName: project.orgName,
                            reviewSystem: this.reviewSystem,
                            reviewProjectIds: this.selectedReviewProjects.map(item => item.reviewProjectId)
                        };
                        if (this.isExpressSystem) {
                            retVal = Object.assign(retVal, { projectLogoBytes: this.logoModel.encodedData });
                            // ECASL-37824 filename is being set to null when it is an empty string as server is treating an empty string filename to be a valid name
                            retVal = Object.assign(retVal, { logoFileName: this.logoModel.fileName === '' ? null : this.logoModel.fileName });
                        }
                        return retVal;
                    },
                    updateStatusType: UpdateStatusType.PROJECT,
                    helpIndex: HelpIndex.edit_case_name_dlg,
                    validateNameRequest: {
                        endpoint: Endpoint.viewManager_validateName,
                        request: (name) => {
                            return {
                                name: name,
                                organizationName: this.commonService.currentUser.customerName,
                                objectType: this.entityData.objectType
                            };
                        }
                    }
                });
            } else if (this.modalData instanceof Corpus) {
                const corpus = this.modalData as Corpus;
                const corpusViewModel = new CorpusViewModel(corpus);
                this.entityData = new CreateEditEntityDataType({
                    entity: corpus,
                    objectType: corpusViewModel.objectType,
                    regex: corpusViewModel.regex,
                    title: isString(corpus.name) ? 'Edit Data Set' : 'New Data Set',
                    isBusyMessage: isString(corpus.name) ? 'Updating Data Set ...' : 'Creating Data Set ...',
                    endpoint: Endpoint.corpusManager_updateCorpusData,
                    request: () => {
                        return {
                            corpusName: this.nameDescriptionComponent.name,
                            description: this.nameDescriptionComponent.description,
                            sharedState: corpus.sharedState,
                            viewHandle: corpus.corpusViewHandle
                        };
                    },
                    updateStatusType: UpdateStatusType.CORPUS,
                    helpIndex: HelpIndex.imports_edit_data_set_name_dlg,
                    validateNameRequest: {
                        endpoint: Endpoint.viewManager_validateName,
                        request: (name) => {
                            return {
                                name: name,
                                projectHandle: this.commonService.currentProject.handle,
                                objectType: this.entityData.objectType
                            };
                        }
                    }
                });
            } else if (this.modalData instanceof AliasList) {
                const selAliasList = this.modalData as AliasList;
                const aliasListViewModel = new AliasListViewModel(selAliasList);
                this.entityData = new CreateEditEntityDataType({
                    entity: selAliasList,
                    objectType: aliasListViewModel.objectType,
                    regex: aliasListViewModel.regex,
                    title: isString(selAliasList.name) ? 'Edit Alias List' : 'New Alias List',
                    endpoint: isString(selAliasList.name) ? Endpoint.projectManager_updateAliasList : Endpoint.projectManager_createAliasList,
                    request: () => {
                        if (this.entityData.isEdit) {
                            return {
                                aliases: selAliasList.aliases,
                                handle: selAliasList.handle,
                                name: this.nameDescriptionComponent.name,
                                description: this.nameDescriptionComponent.description
                            };
                        } else {
                            return {
                                name: this.nameDescriptionComponent.name,
                                parentHandle: this.modalData.parentHandle,
                                description: this.nameDescriptionComponent.description
                            };
                        }
                    },
                    updateStatusType: UpdateStatusType.ALIAS_LISTS,
                    helpIndex: HelpIndex.myedisc_add_edit_alias_list_dlg,
                    validateNameRequest: {
                        endpoint: Endpoint.viewManager_validateName,
                        request: (name) => {
                            return {
                                name: this.nameDescriptionComponent.name,
                                objectType: this.entityData.objectType,
                                projectHandle: this.modalData.parentHandle
                            };
                        }
                    }
                });
            } else if (this.modalData instanceof DomainList) {
                const selDomainList = this.modalData as DomainList;
                const domainListViewModel = new DomainListViewModel(selDomainList);
                const systemScope = (this.modalData as any).isSystemUser;
                this.popoverStickyClass = 'domain-list-reserved-word-popover';
                this.entityData = new CreateEditEntityDataType({
                    entity: selDomainList,
                    objectType: domainListViewModel.objectType,
                    regex: domainListViewModel.regex,
                    title: isString(selDomainList.name) ? 'Edit Domain List' : 'New Domain List',
                    endpoint: isString(selDomainList.name) ? Endpoint.projectManager_updateDomain : Endpoint.projectManager_createDomain,
                    request: () => {
                        if (this.entityData.isEdit) {
                            return {
                                name: this.nameDescriptionComponent.name,
                                description: this.nameDescriptionComponent.description,
                                handle: selDomainList.handle,
                                domainEntries: [],
                                updateOperation: 'ADD_ENTRIES',
                                systemScope: systemScope
                            };
                        } else {
                            return {
                                name: this.nameDescriptionComponent.name,
                                projectHandle: this.modalData.projectHandle,
                                description: this.nameDescriptionComponent.description,
                                systemScope: systemScope,
                                domainEntries: selDomainList.domainEntries ?? [],
                            };
                        }
                    },
                    updateStatusType: UpdateStatusType.DOMAIN_MEMBERSHIP,
                    helpIndex: HelpIndex.myedisc_add_domain_list_dlg,
                    validateNameRequest: {
                        endpoint: Endpoint.viewManager_validateName,
                        request: (name) => {
                            return {
                                name: this.nameDescriptionComponent.name,
                                projectHandle: this.modalData.projectHandle,
                                objectType: this.entityData.objectType,
                                systemScope: systemScope
                            };
                        }
                    }
                });
            } else if (this.modalData instanceof Template) {
                const selTemplate = this.modalData as Template;
                const templateViewModel = new TemplateViewModel(selTemplate);
                const systemScope = (this.modalData as any).isSystemSettingsContext || selTemplate.scope === 'SYSTEM_LEVEL' || (this.modalData as any).isSystemRole;
                this.entityData = new CreateEditEntityDataType({
                    entity: selTemplate,
                    objectType: templateViewModel.objectType,
                    regex: templateViewModel.regex,
                    endpoint: isString(selTemplate.name) ? Endpoint.orgManager_updateTemplate : Endpoint.orgManager_createTemplate,
                    title: isString(selTemplate.name) ? 'Edit Template' : 'New Template',
                    request: () => {
                        if (this.entityData.isEdit) {
                            return {
                                name: this.nameDescriptionComponent.name,
                                handle: selTemplate.handle,
                                type: selTemplate.templateType,
                                description: this.nameDescriptionComponent.description,
                                defaultTemplate: selTemplate.defaultTemplate,
                                systemScope: systemScope
                            };
                        } else {
                            const orgHandle = selTemplate.scope === "ORG_LEVEL" ? selTemplate.orgId : selTemplate.scope === 'SYSTEM_LEVEL' ? 'super_system_customer' : this.commonService?.currentUser?.customerName;
                            const prjHandle = this.commonService.currentProject !== null ? this.commonService.currentProject.handle : null;
                            return {
                                name: this.nameDescriptionComponent.name,
                                type: selTemplate.templateType,
                                description: this.nameDescriptionComponent.description,
                                defaultTemplate: false,
                                scope: selTemplate.scope,
                                ownerHandle: orgHandle,
                                contextHandle: selTemplate.scope === "ORG_LEVEL" || selTemplate.scope === 'SYSTEM_LEVEL' ? orgHandle : prjHandle,
                                systemScope: systemScope
                            };
                        }
                    },
                    validateNameRequest: {
                        endpoint: Endpoint.viewManager_validateName,
                        request: (name) => {
                            return {
                                name: this.nameDescriptionComponent.name,
                                projectHandle: null,
                                objectType: DrObjectType.TEMPLATE,
                                templateType: selTemplate.templateType,
                                organizationName: selTemplate.scope === 'SYSTEM_LEVEL' ? 'DRSystem' : (selTemplate.orgId ? selTemplate.orgId : this.commonService.currentUser.customerName),
                                systemScope: systemScope
                            };
                        }
                    },
                    autoSelectEntity: selTemplate.handle === 'autoSelect',
                    responseObjectName: 'template',
                    helpIndex: HelpIndex.org_add_edit_template_generic_dlg,
                    updateStatusType: UpdateStatusType.TEMPLATE_MEMEBERSHIP
                });
            } else if (this.modalData instanceof LoadFileProjectTemplate) {
                const selTemplate = this.modalData as LoadFileProjectTemplate;
                const selTemplateViewModel = new LoadFileProjectTemplateViewModel(selTemplate);
                this.entityData = new CreateEditEntityDataType({
                    entity: selTemplate,
                    objectType: selTemplateViewModel.objectType,
                    regex: selTemplateViewModel.regex,
                    endpoint: Endpoint.projectManager_setLoadFileMetadataProfile,
                    title: isString(selTemplate.name) ? 'Edit Template' : 'New Template',
                    request: () => {
                        if (this.entityData.isEdit) {
                            return {
                                availableLoadFileFields: selTemplate.availableLoadFileFields,
                                defaultProfile: selTemplate.defaultProfile,
                                description: this.nameDescriptionComponent.description,
                                encodingType: selTemplate.encodingType,
                                entries: selTemplate.entries,
                                loadFileType: selTemplate.loadFileType,
                                loadImages: selTemplate.loadImages,
                                loadMode: 'REPLACE',
                                name: this.nameDescriptionComponent.name,
                                preserveFamily: selTemplate.preserveFamily,
                                profileHandle: selTemplate.id,
                                specialCharacters: selTemplate.specialCharacters,
                                version: selTemplate.version,
                                systemScope: this.modalData?.isSystemSettingsContext
                            };
                        } else {
                            return {
                                availableLoadFileFields: [],
                                defaultProfile: false,
                                description: this.nameDescriptionComponent.description,
                                encodingType: null,
                                entries: [],
                                loadFileType: null,
                                loadImages: false,
                                loadMode: null,
                                name: this.nameDescriptionComponent.name,
                                preserveFamily: true,
                                profileHandle: selTemplate.projectId,
                                specialCharacters: [],
                                version: 1,
                                systemScope: this.modalData?.isSystemSettingsContext
                            };
                        }
                    },
                    validateNameRequest: {
                        endpoint: Endpoint.viewManager_validateName,
                        request: (name) => {
                            return {
                                name: this.nameDescriptionComponent.name,
                                projectHandle: selTemplate.projectId,
                                objectType: this.entityData.objectType,
                                systemScope: this.modalData?.isSystemSettingsContext
                            };
                        }
                    },
                    responseObjectName: 'profile',
                    helpIndex: HelpIndex.org_add_edit_template_generic_dlg,
                    updateStatusType: UpdateStatusType.TEMPLATE_MEMEBERSHIP
                });
            } else if (this.modalData instanceof ExportMetadataProfile) {
                const selTemplate = this.modalData as ExportMetadataProfile;
                const exportViewModel = new ExportMetadataProfileViewModel(selTemplate);
                this.entityData = new CreateEditEntityDataType({
                    entity: selTemplate,
                    objectType: exportViewModel.objectType,
                    regex: exportViewModel.regex,
                    endpoint: isString(selTemplate.name) ? Endpoint.projectManager_setExportMetadataProfile : Endpoint.projectManager_createExportMetadataProfile,
                    title: isString(selTemplate.name) ? 'Edit Template' : 'New Template',
                    request: () => {
                        if (this.entityData.isEdit) {
                            return {
                                defaultProfile: selTemplate.defaultProfile,
                                entries: selTemplate.entries,
                                name: this.nameDescriptionComponent.name,
                                profileHandle: selTemplate.handle,
                                version: selTemplate.version,
                                description: this.nameDescriptionComponent.description,
                                systemScope: this.modalData?.isSystemSettingsContext
                            };
                        } else {
                            return {
                                defaultProfile: false,
                                profileName: this.nameDescriptionComponent.name,
                                projectId: selTemplate.projectId,
                                description: this.nameDescriptionComponent.description,
                                systemScope: this.modalData?.isSystemSettingsContext
                            };
                        }
                    },
                    validateNameRequest: {
                        endpoint: Endpoint.viewManager_validateName,
                        request: (name) => {
                            return {
                                name: this.nameDescriptionComponent.name,
                                projectHandle: selTemplate.projectId,
                                objectType: this.entityData.objectType,
                                systemScope: this.modalData.isSystemSettingsContext
                            };
                        }
                    },
                    responseObjectName: 'profile',
                    helpIndex: HelpIndex.org_add_edit_template_generic_dlg,
                    updateStatusType: UpdateStatusType.TEMPLATE_MEMEBERSHIP
                });
            } else if (this.modalData.data instanceof CustomFieldProfile && this.modalData.handle) {
                const crtProfile = this.modalData.data as CustomFieldProfile;
                const customField = this.modalData as CustomField;
                const myCustomFieldViewModel = new CustomFieldViewModel(customField);
                this.entityData = new CreateEditEntityDataType({
                    title: 'New Custom Field',
                    entity: customField,
                    objectType: myCustomFieldViewModel.objectType,
                    regex: myCustomFieldViewModel.regex,
                    maxNameLength: myCustomFieldViewModel.maxNameLength,
                    endpoint: Endpoint.projectManager_setCustomMetadataFieldsProfile,
                    request: () => {
                        const newCustomField = new CustomField({ name: this.nameDescriptionComponent.name, description: this.nameDescriptionComponent.description });
                        crtProfile.customMetadataFields = new Array<CustomField>(newCustomField);
                        return {
                            profileDto: crtProfile,
                            requestHandle: this.modalData.handle
                        };
                    },
                    updateStatusType: UpdateStatusType.CUSTOM_FIELD,
                    validateNameRequest: {
                        endpoint: Endpoint.viewManager_validateName,
                        request: (name) => {
                            return {
                                name: this.nameDescriptionComponent.name,
                                projectHandle: this.modalData.handle,
                                objectType: this.entityData.objectType
                            };
                        }
                    },
                    helpIndex: HelpIndex.add_custom_field_dlg
                });
            } else if (this.modalData instanceof Custodian) {
                const custodian = this.modalData as Custodian;
                const custodianViewModel = new CustodianViewModel(custodian);
                this.nameDescriptionComponent.name = custodianViewModel.model.name;
                this.nameDescriptionComponent.description = custodianViewModel.model.description;
                this.modalClass += ' custodian-modal';
                this.entityData = new CreateEditEntityDataType({
                    entity: custodianViewModel.model,
                    originalPriority: custodianViewModel.model.priority,
                    objectType: custodianViewModel.objectType,
                    regex: custodianViewModel.regex,
                    title: isString(custodianViewModel.model.name) ? 'Edit Custodian' : 'New Custodian',
                    endpoint: isString(custodianViewModel.model.name) ? Endpoint.ecaManager_updateCustodian : Endpoint.ecaManager_createCustodian,
                    request: () => {
                        if (this.entityData.isEdit) {
                            return {
                                name: this.nameDescriptionComponent.name,
                                handle: custodianViewModel.model.handle,
                                description: this.nameDescriptionComponent.description,
                                priority: custodian.priority,
                            };
                        } else {
                            return {
                                name: this.nameDescriptionComponent.name,
                                projectHandle: this.commonService.currentProject.handle,
                                description: this.nameDescriptionComponent.description,
                                priority: custodian.priority,
                            };
                        }
                    },
                    validateNameRequest: {
                        endpoint: Endpoint.viewManager_validateName,
                        request: (name) => {
                            return {
                                name: this.nameDescriptionComponent.name,
                                projectHandle: this.commonService.currentProject.handle,
                                objectType: this.entityData.objectType
                            };
                        }
                    },
                    helpIndex: HelpIndex.myedisc_add_custodian_dlg,
                    updateStatusType: UpdateStatusType.CUSTODIAN
                });
            } else if (this.modalData instanceof MediaID) {
                const mediaID = this.modalData as MediaID;
                const mediaIDViewModel = new MediaIDViewModel(mediaID);
                this.nameDescriptionComponent.name = mediaIDViewModel.model.name;
                this.nameDescriptionComponent.description = mediaIDViewModel.model.description;
                this.modalClass += ' mediaID-modal';
                this.popoverStickyClass = 'fill-available-width';
                this.entityData = new CreateEditEntityDataType({
                    entity: mediaID,
                    objectType: mediaIDViewModel.objectType,
                    regex: mediaIDViewModel.regex,
                    title: isString(mediaID.name) ? 'Edit MediaID' : 'New MediaID',
                    endpoint: isString(mediaID.name) ? Endpoint.ecaManager_updateMediaid : Endpoint.ecaManager_createMediaid,
                    request: () => {
                        if (this.entityData.isEdit) {
                            return {
                                name: this.nameDescriptionComponent.name,
                                handle: mediaID.handle,
                                description: this.nameDescriptionComponent.description,
                            };
                        } else {
                            return {
                                name: this.nameDescriptionComponent.name,
                                projectHandle: this.commonService.currentProject.handle,
                                description: this.nameDescriptionComponent.description
                            };
                        }
                    },
                    validateNameRequest: {
                        endpoint: Endpoint.viewManager_validateName,
                        request: (name) => {
                            return {
                                name: this.nameDescriptionComponent.name,
                                projectHandle: this.commonService.currentProject.handle,
                                objectType: this.entityData.objectType
                            };
                        }
                    },
                    helpIndex: HelpIndex.add_edit_mediaidview_dlg,
                    updateStatusType: UpdateStatusType.MEDIAID
                })
            } else if (this.modalData instanceof Batch) {
                const batch = this.modalData as Batch;
                const batchViewModel = new BatchViewModel(batch);
                this.nameDescriptionComponent.name = batchViewModel.model.name;
                this.nameDescriptionComponent.description = batchViewModel.model.description;
                this.modalClass += ' batch-modal';
                this.popoverStickyClass = 'fill-available-width';
                this.entityData = new CreateEditEntityDataType({
                    entity: batch,
                    objectType: batchViewModel.objectType,
                    regex: batchViewModel.regex,
                    title: isString(batch.name) ? 'Edit Batch' : 'New Batch',
                    endpoint: isString(batch.name) ? Endpoint.ecaManager_updateBatch : Endpoint.ecaManager_createBatch,
                    request: () => {
                        if (this.entityData.isEdit) {
                            return {
                                name: this.nameDescriptionComponent.name,
                                handle: batch.handle,
                                description: this.nameDescriptionComponent.description,
                            };
                        } else {
                            return {
                                name: this.nameDescriptionComponent.name,
                                projectHandle: this.commonService.currentProject.handle,
                                description: this.nameDescriptionComponent.description
                            };
                        }
                    },
                    validateNameRequest: {
                        endpoint: Endpoint.viewManager_validateName,
                        request: (name) => {
                            return {
                                name: this.nameDescriptionComponent.name,
                                projectHandle: this.commonService.currentProject.handle,
                                objectType: this.entityData.objectType
                            };
                        }
                    },
                    helpIndex: HelpIndex.add_edit_batchview_dlg,
                    updateStatusType: UpdateStatusType.BATCH
                })
            } else if (this.modalData instanceof Profile) {
                const profile = this.modalData as Profile;
                const profileViewModel = new ProfileViewModel(profile);
                this.nameDescriptionComponent.name = profileViewModel.model.name;
                this.nameDescriptionComponent.description = profileViewModel.model.description;
                this.modalClass += ' profile-modal';
                this.entityData = new CreateEditEntityDataType({
                    entity: profile,
                    objectType: profileViewModel.objectType,
                    regex: profileViewModel.regex,
                    title: isString(profile.name) ? 'Edit Synthetic Document' : 'New Synthetic Document',
                    endpoint: isString(profile.name) ? Endpoint.profileManager_updateProfile : Endpoint.projectManager_createProfile,
                    request: () => {
                        if (this.entityData.isEdit) {
                            return {
                                name: this.nameDescriptionComponent.name,
                                handle: profile.handle,
                                description: this.nameDescriptionComponent.description,
                            };
                        } else {
                            return {
                                name: this.nameDescriptionComponent.name,
                                projectHandle: this.commonService.currentProject.handle,
                                description: this.nameDescriptionComponent.description
                            };
                        }
                    },
                    validateNameRequest: {
                        endpoint: Endpoint.viewManager_validateName,
                        request: (name) => {
                            return {
                                name: this.nameDescriptionComponent.name,
                                projectHandle: this.commonService.currentProject.handle,
                                objectType: this.entityData.objectType
                            };
                        }
                    },
                    helpIndex: HelpIndex.myedisc_create_edit_profile_dlg,
                    updateStatusType: UpdateStatusType.PROFILE
                });
            } else if (this.modalData instanceof SavedSearch) {
                const savedSearch = this.modalData as SavedSearch;
                const savedSearchViewModel = new SavedSearchViewModel(savedSearch);
                this.nameDescriptionComponent.name = savedSearchViewModel.model.name;
                this.nameDescriptionComponent.description = savedSearchViewModel.model.description;
                this.modalClass += ' saved-search';
                this.entityData = new CreateEditEntityDataType({
                    entity: savedSearch,
                    objectType: savedSearchViewModel.objectType,
                    regex: savedSearchViewModel.regex,
                    title: isString(savedSearch.name) ? 'Edit Saved Search' : 'Add Saved Search',
                    endpoint: isString(savedSearch.name) ? Endpoint.viewManager_updateActiveRule : Endpoint.projectManager_createActiveRule,
                    request: () => {
                        if (this.entityData.isEdit) {
                            return {
                                name: this.nameDescriptionComponent.name,
                                handle: savedSearch.handle,
                                description: this.nameDescriptionComponent.description,
                                includeFamily: savedSearch.includeFamily,
                                includeMetadata: savedSearch.includeMetadata,
                                includeSynonyms: savedSearch.includeSynonyms,
                                query: savedSearch.searchQuery,
                                targetViewHandle: savedSearch.targetViewHandle
                            };
                        } else {
                            return {
                                name: this.nameDescriptionComponent.name,
                                projectHandle: this.commonService.currentProject.handle,
                                description: this.nameDescriptionComponent.description,
                                corporaFilters: savedSearch.corporaFilters,
                                includeFamily: savedSearch.includeFamily,
                                includeMetadata: savedSearch.includeMetadata,
                                includeSynonyms: savedSearch.includeSynonyms,
                                generateReports: savedSearch.generateReports,
                                query: savedSearch.searchQuery,
                                targetViewHandle: savedSearch.targetViewHandle
                            };
                        }
                    },
                    validateNameRequest: {
                        endpoint: Endpoint.viewManager_validateName,
                        request: (name) => {
                            return {
                                name: this.nameDescriptionComponent.name,
                                projectHandle: this.commonService.currentProject.handle,
                                objectType: this.entityData.objectType
                            };
                        }
                    },
                    helpIndex: HelpIndex.myedisc_save_search_query_dlg,
                    updateStatusType: UpdateStatusType.SAVED_SEARCH
                });
            } else if (this.modalData instanceof Role) {
                this.nameDescriptionComponent.isDescriptionHidden = true;
                const selRole = this.modalData as Role;
                let title, endpoint, newRole, roleViewModel;
                this.popoverStickyClass = 'sticky-position';
                this.modalClass += ' role-modal';
                if (this.options && this.options.copy) {
                    title = 'Copy Role';
                    this.isBusyMessage = `Copying Role ...`;
                    newRole = selRole.copy('', false, false);
                    newRole.handle = selRole.handle;
                    selRole.handle = 'autoSelect';
                    this.nameDescriptionComponent.name = newRole.name;
                    roleViewModel = new RoleViewModel(newRole);
                    endpoint = Endpoint.orgManager_copyRole;
                } else {
                    roleViewModel = new RoleViewModel(selRole);
                    title = isString(selRole.name) ? 'Edit Role' : 'Add Role';
                    endpoint = isString(selRole.name) ? Endpoint.orgManager_updateRole : Endpoint.orgManager_createRole;
                    this.isBusyMessage = isString(selRole.name) ? 'Updating Role ...' : 'Creating Role ...';
                }
                this.entityData = new CreateEditEntityDataType({
                    entity: roleViewModel.model,
                    objectType: roleViewModel.objectType,
                    regex: roleViewModel.regex,
                    title: title,
                    endpoint: endpoint,
                    request: () => {
                        if (this.options && this.options.copy) {
                            return {
                                name: this.nameDescriptionComponent.name,
                                copyFromRoleHandle: newRole.handle,
                                systemScope: this.modalData?.isSystemRole
                            };
                        } else if (this.entityData.isEdit) {
                            return {
                                handle: roleViewModel.model.handle,
                                name: this.nameDescriptionComponent.name,
                                objectActionList: roleViewModel.model.objectActionList,
                                systemScope: this.modalData?.isSystemRole
                            };
                        } else {
                            return {
                                name: this.nameDescriptionComponent.name,
                                objectActionList: roleViewModel.model.objectActionList,
                                systemScope: this.modalData?.isSystemRole
                            };
                        }
                    },
                    validateNameRequest: {
                        endpoint: Endpoint.viewManager_validateName,
                        request: (name) => {
                            return {
                                name: this.nameDescriptionComponent.name,
                                projectHandle: null,
                                objectType: this.entityData.objectType,
                                organizationName: this.modalData.settingsType === 'system' ? 'super_system_customer' : this.commonService.currentUser.customerName,
                                systemScope: this.modalData?.isSystemRole
                            };
                        }
                    },
                    autoSelectEntity: selRole.handle === 'autoSelect',
                    helpIndex: HelpIndex.add_edit_copy_role,
                    updateStatusType: UpdateStatusType.TEMPLATE_MEMEBERSHIP,
                    responseObjectName: 'role',
                });
            } else if (this.modalData instanceof Workflow) {
                const workflow = this.modalData as Workflow;
                const workflowViewModel = new WorkflowViewModel(workflow);
                this.nameDescriptionComponent.name = workflowViewModel.model.name;
                this.nameDescriptionComponent.description = workflowViewModel.model.description;
                this.modalClass += ' workflow';
                let title;
                let endpoint;
                if (this.options && this.options.copy) {
                    title = 'Copy Workflow';
                    endpoint = Endpoint.workFlowManager_copyWorkFlow;
                } else {
                    title = this.options ? this.options : (isString(workflow.name) ? 'Edit Workflow' : 'Add Workflow');
                    endpoint = isString(workflow.name) ? Endpoint.workFlowManager_updateWorkFlow : Endpoint.workFlowManager_createWorkFlow;
                }
                this.entityData = new CreateEditEntityDataType({
                    entity: workflow,
                    objectType: workflowViewModel.objectType,
                    regex: workflowViewModel.regex,
                    title: title,
                    endpoint: endpoint,
                    isBusyMessage: this.options && this.options.copy ? 'Copying Workflow ...' : null,
                    request: () => {
                        if (this.options && this.options.copy) {
                            return {
                                name: this.nameDescriptionComponent.name,
                                projectHandle: this.commonService.currentProject.handle,
                                description: this.nameDescriptionComponent.description,
                                sourceWorkFlowHandle: workflow.handle
                            };
                        } else if (this.entityData.isEdit) {
                            return {
                                name: this.nameDescriptionComponent.name,
                                handle: workflow.handle,
                                description: this.nameDescriptionComponent.description,
                                workFlow: {
                                    createdBy: workflow.createdBy,
                                    createdOn: workflow.createdOn,
                                    dataSetItem: workflow.dataSetItem ? {
                                        attributes: workflow.dataSetItem.attributes,
                                        description: workflow.dataSetItem.description,
                                        handle: workflow.dataSetItem.handle,
                                        name: workflow.dataSetItem.name,
                                        type: 'DATASET'
                                    } : null,
                                    description: this.nameDescriptionComponent.description,
                                    handle: workflow.handle,
                                    modifiedBy: workflow.modifiedBy,
                                    modifiedOn: workflow.modifiedOn,
                                    name: this.nameDescriptionComponent.name,
                                    projectHandle: (this.commonService.currentProject === null && this.entityData?.entity.isParent) ? this.entityData?.entity?.projectHandle : this.commonService.currentProject.handle,
                                    queryItems: {
                                        items: workflow.queryItems.items
                                    },
                                    sourceViewHandle: null,
                                    version: workflow.version,
                                    tagItem: workflow.tagItem ? {
                                        type: 'TAG',
                                        tags: workflow.tagItem.tags,
                                        orderOfExecution: workflow.queryItems.items && workflow.queryItems.items.length > 0 ?
                                            workflow.queryItems.items.length + 1 : 1
                                    } : null,
                                    folderItem: workflow.folderItem && workflow.folderItem.attributes ? {
                                        attributes: workflow.folderItem.attributes.length > 0 ? [{ name: 'folderColor', value: workflow.folderItem.attributes[0].value }] : [],
                                        type: 'FOLDER',
                                        handle: workflow.folderItem.handle,
                                        name: workflow.folderItem.name,
                                        description: workflow.folderItem.description,
                                        orderOfExecution: workflow.tagItem && workflow.tagItem.tags.length > 0 ? workflow.queryItems.items.length + 2 : 2,
                                    } : null
                                }
                            };
                        } else {
                            return {
                                name: this.nameDescriptionComponent.name,
                                projectHandle: this.commonService.currentProject.handle,
                                description: this.nameDescriptionComponent.description
                            };
                        }
                    },
                    validateNameRequest: {
                        endpoint: Endpoint.viewManager_validateName,
                        request: (name) => {
                            return {
                                name: this.nameDescriptionComponent.name,
                                projectHandle: ((this.commonService.currentProject === null && this.entityData?.entity?.isParent) ? this.entityData?.entity?.projectHandle : this.commonService.currentProject.handle) || workflow.projectHandle,
                                objectType: this.entityData.objectType
                            };
                        }
                    },
                    helpIndex: HelpIndex.myedisc_create_workflow_dlg,
                    updateStatusType: UpdateStatusType.WORK_FLOW
                });
            } else if (this.modalData instanceof ExportStream) {
                const exportStream = this.modalData as ExportStream;
                const exportStreamViewModel = new ExportStreamViewModel(exportStream);
                this.nameDescriptionComponent.name = exportStreamViewModel.model.name;
                this.nameDescriptionComponent.description = exportStreamViewModel.model.description;
                this.modalClass += ' export-stream-modal';
                this.entityData = new CreateEditEntityDataType({
                    entity: exportStream,
                    objectType: exportStreamViewModel.objectType,
                    regex: exportStreamViewModel.regex,
                    title: 'Edit Export Stream',
                    endpoint: Endpoint.viewManager_updateView,
                    request: () => {
                        if (this.entityData.isEdit) {
                            return {
                                name: this.nameDescriptionComponent.name,
                                handle: exportStreamViewModel.model.handle,
                                description: this.nameDescriptionComponent.description,
                            };
                        }
                    },
                    validateNameRequest: {
                        endpoint: Endpoint.viewManager_validateName,
                        request: (name) => {
                            return {
                                name: this.nameDescriptionComponent.name,
                                projectHandle: this.commonService.currentProject.handle,
                                objectType: this.entityData.objectType
                            };
                        }
                    },
                    helpIndex: HelpIndex.exports_edit_export_name_desc_dlg,
                    updateStatusType: UpdateStatusType.EXPORT_VIEW
                });
            } else if (this.modalData instanceof Service) {
                const service = this.modalData as Service;
                service.name = service.serviceName;
                const serviceViewModel = new ServiceViewModel(service);
                this.nameDescriptionComponent.name = serviceViewModel?.model?.serviceName;
                this.nameDescriptionComponent.description = serviceViewModel?.model?.description;
                this.nameDescriptionComponent.isNameDisabled = isString(service.serviceName);

                this.entityData = new CreateEditEntityDataType({
                    entity: service,
                    objectType: serviceViewModel.objectType,
                    regex: SupportedRegExType.GENERIC,
                    title: isString(service.serviceName) ? 'Edit Service' : 'New Service',
                    isBusyMessage: isString(service.serviceName) ? 'Updating Service ...' : 'Creating Service ...',
                    endpoint: isString(service.serviceName) ? Endpoint.serviceManager_updateService : Endpoint.realmManager_createService,
                    request: () => {
                        return {
                            serviceName: this.nameDescriptionComponent.name,
                            serviceDescription: this.nameDescriptionComponent.description,
                            serviceExpressNodes: !isString(service.serviceName) ? [] : serviceViewModel?.model?.serviceExpressNodes,
                            serviceOcrNodes: !isString(service.serviceName) ? [] : serviceViewModel?.model?.serviceOcrNodes,
                            serviceRealmNodes: !isString(service.serviceName) ? [] : serviceViewModel?.model?.serviceRealmNodes,
                            requestHandle: !isString(service.serviceName) ? null : serviceViewModel?.model?.handle,
                            contextHandle: 'super_system_customer'
                        };
                    },
                    updateStatusType: UpdateStatusType.SERVICE,
                    helpIndex: HelpIndex.sys_add_edit_services_dlg,
                    validateNameRequest: {
                        endpoint: Endpoint.viewManager_validateName,
                        request: (name) => {
                            return {
                                name: this.nameDescriptionComponent.name,
                                objectType: DrObjectType.SERVICE,
                                systemScope: true
                            };
                        }
                    }
                });
            } else if (this.modalData instanceof DataArea) {
                const dataArea = this.modalData as DataArea;
                const dataAreaViewModel = new DataAreaViewModel(dataArea);
                this.nameDescriptionComponent.name = dataAreaViewModel?.model.name;
                this.nameDescriptionComponent.description = dataAreaViewModel?.model?.description;

                this.entityData = new CreateEditEntityDataType({
                    entity: dataArea,
                    objectType: DrObjectType.EXPORT_DATA_AREA,
                    regex: SupportedRegExType.DATAAREA,
                    title: 'Edit Reef Review Export Data Area',
                    isBusyMessage: 'Updating Reef Review Export Data Area ...',
                    endpoint: Endpoint.dataAreaManager_updateDataArea,
                    request: () => {
                        return {
                            name: this.nameDescriptionComponent.name,
                            description: this.nameDescriptionComponent.description,
                            mode: 'EXPORT',
                            connectorHandle: this.modalData.connectorHandle,
                            path: this.modalData.path,
                            handle: this.modalData.handle
                        };
                    },
                    updateStatusType: UpdateStatusType.DATA_AREA,
                    validateNameRequest: {
                        endpoint: Endpoint.viewManager_validateName,
                        request: (name) => {
                            return {
                                name: this.nameDescriptionComponent.name,
                                objectType: this.nameDescriptionComponent.drObject.objectType
                            };
                        }
                    }
                });
            } else if (this.modalData instanceof Organization) {
                const org = this.modalData as Organization;
                const orgViewModel = new OrganizationViewModel(org);
                this.nameDescriptionComponent.name = orgViewModel?.model.organizationName;
                this.nameDescriptionComponent.description = orgViewModel?.model?.description;

                this.entityData = new CreateEditEntityDataType({
                    entity: org,
                    objectType: DrObjectType.ORGANIZATION,
                    regex: SupportedRegExType.ORGANIZATION,
                    title: 'Edit Organization',
                    isBusyMessage: 'Updating Organization ...',
                    endpoint: Endpoint.adminOrgManager_updateOrganization,
                    request: () => {
                        return {
                            name: this.nameDescriptionComponent.name,
                            description: this.nameDescriptionComponent.description,
                            organizationHandle: this.modalData.handle,
                            systemScope: true
                        };
                    },
                    updateStatusType: UpdateStatusType.ORGANIZATION,
                    validateNameRequest: {
                        endpoint: Endpoint.viewManager_validateName,
                        request: (name) => {
                            return {
                                name: this.nameDescriptionComponent.name,
                                objectType: this.nameDescriptionComponent.drObject.objectType
                            };
                        }
                    }
                });
            }
        }
        this.initializeNameDescriptionData(this.entityData);
    }

    ok(): void {
        if (this.entityData) {
            this.nameDescriptionComponent.validate().then(valid => {
                if (valid || (!this.nameDescriptionComponent.isNameDirty() && this.entityData.entity instanceof Custodian) ||
                    (!this.nameDescriptionComponent.isNameDirty() && this.entityData.entity instanceof Project &&
                        (this.isSelectedReviewProjectsDirty() || this.isLogoFileDirty() || this.isReviewSystemDirty()))) {
                    const request = isFunction(this.entityData.request) ? this.entityData.request() : this.entityData.request;
                    if (request) {
                        this.isBusy = true;
                        this.isBusyMessage = this.entityData.isBusyMessage;
                        if (this.modalData instanceof Role && this.entityData.endpoint === Endpoint.orgManager_createRole) {
                            request.organizationName = this.modalData.settingsType === 'system' ? 'super_system_customer' : this.commonService.currentUser.customerName;
                        }
                        this.restService.post(this.entityData.endpoint, request).then(response => {
                            if (this.entityData.objectType === DrObjectType.PROJECT) {
                                this.commonService.sendMessage(MessageType.project);
                                this.close(response);
                            } else if (this.entityData.updateStatusType) {
                                let autoSelect, handle;
                                if (this.entityData.autoSelectEntity || this.modalData instanceof ExportMetadataProfile) {
                                    autoSelect = true;
                                    handle = this.entityData.responseObjectName ? this.entityData.responseObjectHandleName : null;
                                } else if (this.modalData instanceof LoadFileProjectTemplate) {
                                    autoSelect = true;
                                    handle = 'id';
                                }
                                if (autoSelect && this.entityData.responseObjectName && handle) {
                                    const createdObject = response[this.entityData.responseObjectName];
                                    if (createdObject && createdObject[handle]) {
                                        this.commonService.sendMessage(MessageType.entityChanged, {
                                            updateStatusType: this.entityData.updateStatusType,
                                            autoSelectHandle: createdObject[handle]
                                        });
                                    } else {
                                        this.commonService.sendMessage(MessageType.entityChanged, this.entityData.updateStatusType);
                                    }
                                    this.close(response);
                                } else if (this.entityData.endpoint === Endpoint.workFlowManager_createWorkFlow) {
                                    const projectData = this.commonService.projectData;
                                    setTimeout(() => {
                                        this.restService.post(Endpoint.workFlowManager_updateWorkFlow, {
                                            name: response.workFlow.name,
                                            handle: response.workFlow.handle,
                                            description: response.workFlow.description,
                                            workFlow: {
                                                createdBy: response.workFlow.createdBy,
                                                createdOn: response.workFlow.createdOn,
                                                dataSetItem: {
                                                    attributes: projectData.attributes,
                                                    description: projectData.description,
                                                    handle: projectData.handle,
                                                    name: projectData.name,
                                                    type: 'DATASET'
                                                },
                                                description: response.workFlow.description,
                                                handle: response.workFlow.handle,
                                                modifiedBy: response.workFlow.modifiedBy,
                                                modifiedOn: response.workFlow.modifiedOn,
                                                name: response.workFlow.name,
                                                projectHandle: this.commonService.currentProject.handle,
                                                sourceViewHandle: null,
                                                version: 1
                                            }
                                        }).then(() => {
                                            this.commonService.sendMessage(MessageType.entityChanged, this.entityData.updateStatusType);
                                            this.close(response);
                                        }, error => {
                                            this.isBusy = false;
                                        });
                                    });
                                } else {
                                    this.commonService.sendMessage(MessageType.entityChanged, this.entityData.updateStatusType);
                                    this.close(response);
                                }
                            } else {
                                this.close(response);
                            }
                        }, error => {
                            this.isBusy = false;
                        });
                    }
                } else {
                    this.isBusy = false;
                }
            });
        }
    }

    private close(response: any): void {
        // the order of these events is important...do not switch them around.
        this.closeModal(true, this.entityData.objectType === DrObjectType.SAVED_SEARCH ? { name: this.nameDescriptionComponent.name, entityType: UpdateStatusType.SAVED_SEARCH } : response);
        this.nameDescriptionComponent.formValid = false;
        this.modalOpened = false;
        this.isBusy = false;
    }

    okEnabled(): boolean {
        let enabled = false;
        if (!this.isBusy) {
            if (!DrObject.isEmpty(this.nameDescriptionComponent.name)) {
                if (this.nameDescriptionComponent.isNameDirty() && (this.nameDescriptionComponent.formValid)) {
                    enabled = true;
                } else if ((this.nameDescriptionComponent.isDescriptionDirty()) && (this.nameDescriptionComponent.formValid)) {
                    enabled = true;
                } else if (!DrObject.isEmpty(this.nameDescriptionComponent.name) && !this.nameDescriptionComponent.isDirty() && this.isPriorityDirty()) {
                    enabled = true;
                } else if (!DrObject.isEmpty(this.nameDescriptionComponent.name) && !this.nameDescriptionComponent.isDirty() && this.isSelectedReviewProjectsDirty()) {
                    enabled = true;
                } else if (!DrObject.isEmpty(this.nameDescriptionComponent.name) && !this.nameDescriptionComponent.isDirty() && this.isLogoFileDirty()) {
                    enabled = true;
                } else if (!DrObject.isEmpty(this.nameDescriptionComponent.name) && !this.nameDescriptionComponent.isDirty() && this.isReviewSystemDirty()) {
                    enabled = true;
                }
            }
            if (this.entityData.entity instanceof Project && (!this.userValid ||
                ((this.reviewSystem === ReviewSystem.REEF_REVIEW || this.reviewSystem === ReviewSystem.RELATIVITY) && this.selectedReviewProjects?.length === 0))) {
                enabled = false;
            }
        }
        return enabled;
    }

    initializeNameDescriptionData(entity: DrObject) {
        if (entity) {
            this.nameDescriptionComponent.drObject = entity;
        }
    }

    isPriorityDirty(): boolean {
        let isDirty = false;
        if ((!isUndefined(this.entityData.entity) && (this.entityData.entity instanceof Custodian) && (!isUndefined(this.entityData.originalPriority)))) {
            if (this.entityData.entity.priority !== this.entityData.originalPriority) {
                isDirty = true;
            }
        }
        return isDirty;
    }

    isReviewSystemDirty(): boolean {
        let isDirty = false;
        if ((!isUndefined(this.entityData.entity) && (this.entityData.entity instanceof Project) && (!isUndefined(this.entityData.originalReviewSystem)))) {
            if (this.reviewSystem !== this.entityData.originalReviewSystem) {
                isDirty = true;
            }
        }
        return isDirty;
    }

    isSelectedReviewProjectsDirty(): boolean {
        let isDirty = false;
        if (!isUndefined(this.entityData.entity) && (this.entityData.entity instanceof Project) && this.entityData.originalSelectedReviewProjects?.length > 0) {
            if (this.entityData.originalSelectedReviewProjects.length !== this.selectedReviewProjects.length) return true;

            const ids1 = new Set(this.entityData.originalSelectedReviewProjects.map(item => item.reviewProjectId));
            const ids2 = new Set(this.selectedReviewProjects.map(item => item.reviewProjectId));

            return this.selectedReviewProjects.some(item => !ids1.has(item.reviewProjectId));
        }
        return isDirty;
    }

    isLogoFileDirty(): boolean {
        let isDirty = false;
        if ((!isUndefined(this.entityData.entity) && (this.entityData.entity instanceof Project) && (!isUndefined(this.entityData.originalFileMD5)))) {
            if (this.logoModel.fileMD5 !== this.entityData.originalFileMD5) {
                isDirty = true;
            }
        }
        return isDirty;
    } 

    onChange(selectedReviewSystem: ReviewSystem) {
        if (selectedReviewSystem === this.reviewSystem) {
            return;
        }
        this.userValid = true;
        this.availableReviewProjects = [];
        this.reviewSystem = selectedReviewSystem;
        if (selectedReviewSystem === ReviewSystem.REEF_REVIEW) {
            this.isBusy = true;
            this.isBusyMessage = 'Validating Current User ...';
            this.userValid = false;
            this.restService.post(Endpoint.projectManager_validateReviewUser, { userEmail: this.commonService.currentUser.email, reviewSystem: selectedReviewSystem }).then(response => {
                if (response?.valid) {
                    this.userValid = true;
                } else {
                    this.commonService.openErrorModal(response.extendedStatus);
                }
                this.isBusy = false;
                this.isBusyMessage = ``;
            }, () => {
                this.isBusy = false;
            });
        }
    }

    onProjectTypeChanged() {
        if (this.standardProjectType && this.logoModel.encodedData !== null) {
            this.logoModel.encodedData = null;
            this.logoModel.fileName = "";
        }
    }

    openSelectReviewProjects(): void {
        this.getAvailableProjectIDs().then(validProjectID => {
            if (this.modalOpened) {
                this.availableReviewProjects.forEach(project => project.assigned = false);
                this.selectedReviewProjects.forEach(selectedProject => {
                    const matchedItem = this.availableReviewProjects.find(item => item.reviewProject.id === selectedProject.reviewProjectId);
                    if (matchedItem) {
                        matchedItem.assigned = true;
                    }
                })
                this.commonService.openModal(SelectReviewProjectsModalComponent, {
                    class: 'horizontal-percent-60 vertical-percent-60',
                    modalData: { items: this.availableReviewProjects, reviewSystem: this.reviewSystem }
                }).then((result) => {
                    if (result.modalData?.length > 0) {
                        result.modalData.forEach(data => {
                            const foundReviewProject = this.selectedReviewProjects.find(item => item.reviewProjectId === data.reviewProject.id);
                            if (!foundReviewProject) {
                                data.reviewProjectName = data.reviewProject.name;
                                data.reviewProjectId = data.reviewProject.id;
                                this.selectedReviewProjects.push(data);
                            }
                        });
                    }
                });
            }
        })
    }

    getAvailableProjectIDs(): Promise<any> {
        if (!this.availableReviewProjects || this.availableReviewProjects.length === 0) {
            this.isBusy = true;
            this.isBusyMessage = 'Loading ...';
            return new Promise<any>((resolve) => {
                this.restService.postWithObservable(Endpoint.projectManager_getReviewProjectList,
                    {
                        reviewSortBy: 'REVIEW_PROJECT_NAME',
                        reviewSystem: this.reviewSystem,
                    }).subscribe({
                        next: (response: any) => {
                            this.isBusy = false;
                            if (response?.reviewClientProjects) {
                                this.availableReviewProjects = response.reviewClientProjects;
                            }
                            return resolve(true);
                        }
                    });
            });
        } else {
            return Promise.resolve(true);
        }
    }

    onRemoveClick(item): void {
        const index = this.selectedReviewProjects.findIndex(prj => prj.reviewProjectId === item.reviewProjectId);
        if (index > -1) {
            if (this.entityData.originalSelectedReviewProjects.find(prj => prj.reviewProjectId === item.reviewProjectId)) {
                this.commonService.openErrorModal('Removing review project associations is not yet supported.');
            } else {
                this.selectedReviewProjects.splice(index, 1);
            }
        }
    }
}
